/**
 * React Query hooks for the student calendar + booking flow.
 *
 * These are the only place where react-query is wired to the API client
 * (`src/api/client.ts`); the screens consume the hooks and never talk to
 * `fetch` directly. That way every server interaction in the calendar
 * surface goes through one consistent layer with one consistent
 * cache-invalidation story.
 *
 * Cache shape
 * -----------
 *
 *   ['lessons', { from, to }]    →  Lesson[]   (list_lessons range)
 *   ['lesson', id]               →  Lesson     (single lesson, issue #273)
 *   ['lessons', id, 'students']  →  Student[]  (enrollment, teacher)
 *   ['me', 'reservations']       →  Lesson[]   (list_my_reservations)
 *
 * Range keys are inclusive on both ends. We pass them as ISO-8601
 * strings so a fresh `useLessons({ from, to })` call with the same
 * string pair hits the cache; a different range re-fetches.
 *
 * Mutations (book / cancel) flip the relevant lessons entries
 * *optimistically* and roll back on error. The `onSettled` hook
 * always re-invalidates so the server is the final source of truth
 * — see the acceptance criteria ("no stale counts").
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { ApiRequestError, api } from "./client";
import type {
  ApiErrorTag,
  AnalyticsLessonRow,
  AnalyticsOverview,
  EnrollmentError,
  Lesson,
  Membership,
  ReservationError,
  Student,
  StudentCreditSummary,
  StudentNote,
  Teacher,
  TeacherCreditSummary,
} from "../lib/types";

export interface LessonsRange {
  /** ISO-8601 UTC datetime, inclusive. */
  from: string;
  /** ISO-8601 UTC datetime, inclusive. */
  to: string;
}

export const queryKeys = {
  lessons: (range: LessonsRange) => ["lessons", range] as const,
  /**
   * A single lesson by id (issue #273). Kept in the singular
   * `['lesson', ...]` namespace so the `['lessons']` prefix matcher used
   * by the list mutations does not confuse the two shapes.
   */
  lesson: (id: number) => ["lesson", id] as const,
  /** Top-level matcher for "any lessons query" — used by mutations. */
  lessonsAll: () => ["lessons"] as const,
  myReservations: () => ["me", "reservations"] as const,
  students: () => ["students"] as const,
  /** The teacher list (issue #284), for the lesson form's instructor picker. */
  teachers: () => ["teachers"] as const,
  studentNotes: (studentId: number) => ["students", studentId, "notes"] as const,
  /** A student's bookings, by scope (teacher, issue #223). */
  studentReservations: (studentId: number, scope: StudentReservationsScope) =>
    ["students", studentId, "reservations", scope] as const,
  /** Roster of students enrolled in a lesson (teacher, issue #184). */
  lessonStudents: (lessonId: number) => ["lessons", lessonId, "students"] as const,
  /** The caller's sanitized credit summary (issue #246). */
  myCredits: () => ["me", "credits"] as const,
  /** A student's full credit ledger (teacher, issue #246). */
  studentCredits: (studentId: number) => ["students", studentId, "credits"] as const,
  /** A student's membership periods (teacher, issue #246). */
  studentMemberships: (studentId: number) =>
    ["students", studentId, "memberships"] as const,
  /** The studio analytics dashboard overview (teacher, issue #292). */
  analytics: (range: AnalyticsRange) => ["analytics", "overview", range] as const,
  /** The analytics drill-down lesson rows (teacher, issue #292). */
  analyticsLessons: (range: AnalyticsRange, filters: AnalyticsLessonFilters) =>
    ["analytics", "lessons", range, filters] as const,
} as const;

/**
 * GET /api/lessons?from=&to=
 *
 * Returns the lessons in `range` decorated with `spots_taken`,
 * `spots_left`, and `reserved_by_me` for the current user. The hook
 * is gated on `token` so an anonymous render doesn't fire a
 * request that would 401.
 */
export function useLessons(
  token: string | null,
  range: LessonsRange,
): UseQueryResult<Lesson[], ApiRequestError> {
  return useQuery<Lesson[], ApiRequestError>({
    queryKey: queryKeys.lessons(range),
    queryFn: ({ signal }) =>
      api.get<Lesson[]>("/api/lessons", {
        token,
        signal,
        query: { from: range.from, to: range.to },
      }),
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}

/**
 * GET /api/lessons/:id
 *
 * Returns a single lesson decorated with `spots_taken`, `spots_left` and
 * `reserved_by_me` for the current user (issue #273). Unlike `useLessons`
 * this has no date window, so a bookmarked / shared deep link resolves
 * even for a past lesson or one beyond the default 14-day range. Gated on
 * `token` so an anonymous render doesn't fire a request that would 401.
 */
export function useLesson(
  token: string | null,
  lessonId: number | null,
): UseQueryResult<Lesson, ApiRequestError> {
  return useQuery<Lesson, ApiRequestError>({
    queryKey: queryKeys.lesson(lessonId ?? 0),
    queryFn: ({ signal }) =>
      api.get<Lesson>(`/api/lessons/${lessonId}`, { token, signal }),
    enabled: Boolean(token) && lessonId !== null,
    staleTime: 30_000,
  });
}

/**
 * GET /api/me/reservations
 *
 * Returns the current student's upcoming booked lessons, ordered by
 * `starts_at` ascending. Each entry has `reserved_by_me: true` because
 * the list is filtered to the requesting user.
 */
export function useMyReservations(
  token: string | null,
): UseQueryResult<Lesson[], ApiRequestError> {
  return useQuery<Lesson[], ApiRequestError>({
    queryKey: queryKeys.myReservations(),
    queryFn: ({ signal }) =>
      api.get<Lesson[]>("/api/me/reservations", { token, signal }),
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}

/**
 * GET /api/students
 *
 * Teacher roster (issue #89): every student account ordered by email,
 * decorated with upcoming/total booking counts. The endpoint is
 * teacher-only, so this is gated on the caller being a teacher.
 */
export function useStudents(
  token: string | null,
  enabled: boolean,
): UseQueryResult<Student[], ApiRequestError> {
  return useQuery<Student[], ApiRequestError>({
    queryKey: queryKeys.students(),
    queryFn: ({ signal }) =>
      api.get<Student[]>("/api/students", { token, signal }),
    enabled: Boolean(token) && enabled,
    staleTime: 30_000,
  });
}

/**
 * GET /api/teachers
 *
 * The teacher list (issue #284): backs the lesson form's instructor
 * picker so a teacher can assign a lesson to a colleague. Teacher-only,
 * so it is gated on `token` (the `/teacher` route already requires the
 * role).
 */
export function useTeachers(
  token: string | null,
): UseQueryResult<Teacher[], ApiRequestError> {
  return useQuery<Teacher[], ApiRequestError>({
    queryKey: queryKeys.teachers(),
    queryFn: ({ signal }) =>
      api.get<Teacher[]>("/api/teachers", { token, signal }),
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}

/**
 * The reservation-specific 422 contract tags from `docs/API_CONTRACT.md`.
 * Surfaced through `error.body?.error` by `api/client.ts`.
 */
const RESERVATION_ERRORS: ReadonlySet<ApiErrorTag> = new Set([
  "full",
  "already_booked",
  "too_late",
  "too_early",
  "not_bookable",
  "insufficient_credits",
]);

export function reservationError(err: unknown): ReservationError | undefined {
  if (err instanceof ApiRequestError) {
    const tag = err.body?.error;
    return tag && RESERVATION_ERRORS.has(tag) ? (tag as ReservationError) : undefined;
  }
  return undefined;
}

/**
 * The enrollment 422 contract tags (issues #184/#245). Teacher enrollment
 * into an `:assigned` lesson can fail with `no_active_membership`, and into
 * an `:open` one with `insufficient_credits`.
 */
const ENROLLMENT_ERRORS: ReadonlySet<ApiErrorTag> = new Set([
  "full",
  "already_enrolled",
  "no_active_membership",
  "insufficient_credits",
]);

export function enrollmentError(err: unknown): EnrollmentError | undefined {
  if (err instanceof ApiRequestError) {
    const tag = err.body?.error;
    return tag && ENROLLMENT_ERRORS.has(tag) ? (tag as EnrollmentError) : undefined;
  }
  return undefined;
}

/**
 * POST /api/lessons/:id/reservations
 *
 * Optimistic flow: snapshotted query data, the matching lesson is
 * bumped to `reserved_by_me: true` and `spots_left` is decremented
 * before the request resolves. On error, the snapshot is restored.
 * `onSettled` always invalidates the relevant queries so the cache
 * stays consistent with the server (acceptance: "no stale counts").
 */
export function useBookLesson(
  token: string | null,
): UseMutationResult<void, ApiRequestError, number> {
  const qc = useQueryClient();

  return useMutation<void, ApiRequestError, number, OptimisticCtx>({
    mutationFn: (lessonId) =>
      api.post<void>(`/api/lessons/${lessonId}/reservations`, {}, { token }),
    onMutate: async (lessonId) => {
      // Cancel any in-flight fetches so they don't clobber the optimistic
      // write once they return — both the list and the single-lesson
      // (detail page, issue #273) caches.
      await qc.cancelQueries({ queryKey: queryKeys.lessonsAll() });
      await qc.cancelQueries({ queryKey: queryKeys.lesson(lessonId) });

      const snapshots = qc.getQueriesData<Lesson[]>({
        queryKey: queryKeys.lessonsAll(),
      });
      const mySnapshot = qc.getQueryData<Lesson[]>(queryKeys.myReservations());
      const lessonSnapshot = qc.getQueryData<Lesson>(queryKeys.lesson(lessonId));

      qc.setQueriesData<Lesson[]>(
        { queryKey: queryKeys.lessonsAll() },
        (old) => applyBookOptimistic(old, lessonId),
      );
      qc.setQueryData<Lesson>(queryKeys.lesson(lessonId), (old) =>
        old ? optimisticBook(old) : old,
      );

      return { snapshots, mySnapshot, lessonSnapshot };
    },
    onError: (err, lessonId, ctx) => {
      if (!ctx) return;
      // `already_booked` is a *truthful* state — the user really is
      // booked into this lesson, so the optimistic update was correct.
      // The screen should keep the booked state and the toast should
      // be a friendly heads-up. Any other error rolls back.
      if (reservationError(err) === "already_booked") {
        return;
      }
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
      qc.setQueryData(queryKeys.myReservations(), ctx.mySnapshot);
      qc.setQueryData(queryKeys.lesson(lessonId), ctx.lessonSnapshot);
    },
    onSettled: (_data, _err, lessonId) => {
      qc.invalidateQueries({ queryKey: queryKeys.lessonsAll() });
      qc.invalidateQueries({ queryKey: queryKeys.lesson(lessonId) });
      qc.invalidateQueries({ queryKey: queryKeys.myReservations() });
      // An open booking spends a credit (issue #245), so the caller's
      // sanitized balance must refresh too.
      qc.invalidateQueries({ queryKey: queryKeys.myCredits() });
    },
  });
}

/**
 * DELETE /api/lessons/:id/reservations
 *
 * Same shape as `useBookLesson` but the optimistic update flips
 * `reserved_by_me` back to `false` and frees a spot.
 */
export function useCancelLesson(
  token: string | null,
): UseMutationResult<void, ApiRequestError, number> {
  const qc = useQueryClient();

  return useMutation<void, ApiRequestError, number, OptimisticCtx>({
    mutationFn: (lessonId) =>
      api.delete<void>(`/api/lessons/${lessonId}/reservations`, { token }),
    onMutate: async (lessonId) => {
      await qc.cancelQueries({ queryKey: queryKeys.lessonsAll() });
      await qc.cancelQueries({ queryKey: queryKeys.lesson(lessonId) });

      const snapshots = qc.getQueriesData<Lesson[]>({
        queryKey: queryKeys.lessonsAll(),
      });
      const mySnapshot = qc.getQueryData<Lesson[]>(queryKeys.myReservations());
      const lessonSnapshot = qc.getQueryData<Lesson>(queryKeys.lesson(lessonId));

      qc.setQueriesData<Lesson[]>(
        { queryKey: queryKeys.lessonsAll() },
        (old) => applyCancelOptimistic(old, lessonId),
      );
      qc.setQueryData<Lesson>(queryKeys.lesson(lessonId), (old) =>
        old ? optimisticCancel(old) : old,
      );

      // Drop the cancelled lesson from `my reservations` immediately.
      qc.setQueryData<Lesson[]>(queryKeys.myReservations(), (old) =>
        (old ?? []).filter((l) => l.id !== lessonId),
      );

      return { snapshots, mySnapshot, lessonSnapshot };
    },
    onError: (_err, lessonId, ctx) => {
      if (!ctx) return;
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
      qc.setQueryData(queryKeys.myReservations(), ctx.mySnapshot);
      qc.setQueryData(queryKeys.lesson(lessonId), ctx.lessonSnapshot);
    },
    onSettled: (_data, _err, lessonId) => {
      qc.invalidateQueries({ queryKey: queryKeys.lessonsAll() });
      qc.invalidateQueries({ queryKey: queryKeys.lesson(lessonId) });
      qc.invalidateQueries({ queryKey: queryKeys.myReservations() });
      // Cancelling refunds the credit (issue #245).
      qc.invalidateQueries({ queryKey: queryKeys.myCredits() });
    },
  });
}

/**
 * Teacher-side lesson management.
 *
 * `POST` / `PATCH` / `DELETE /api/lessons[/:id]` are restricted to
 * teachers by the API. They have no per-user optimistic state to keep
 * in sync (the way book/cancel does), so we keep the flow simple:
 * fire the request, and on success invalidate every `lessons` query so
 * the calendar re-fetches and the change is reflected immediately
 * (acceptance: "see it reflected immediately").
 *
 * Validation failures surface as `ApiRequestError` with the field-keyed
 * `{"errors": {field: [msg, ...]}}` body the contract documents; the
 * screen reads `error.body.errors` to render messages inline.
 */
export interface LessonInput {
  title: string;
  description: string | null;
  /**
   * Lesson type (issue #183): `"open"` (students self-book) or
   * `"assigned"` (teacher-enrolled). Create-time only — the server
   * ignores it on PATCH.
   */
  lesson_type: "open" | "assigned";
  /** ISO-8601 UTC datetime, e.g. "2026-06-23T10:00:00Z". */
  starts_at: string;
  duration_minutes: number;
  capacity: number;
  /**
   * Minutes before `starts_at` that reservations close. `null` clears
   * the per-lesson override and inherits the global default (60).
   */
  booking_cutoff_minutes: number | null;
  /**
   * Days before `starts_at` that reservations open. `null` = open
   * immediately (no early restriction).
   */
  booking_open_days: number | null;
  /**
   * Weekly series (issue #59): `{ occurrences: N }` (1..52) materializes
   * N weekly lessons at the same day/time as `starts_at`. `null` = a
   * one-off lesson. Create-time only — ignored on PATCH.
   */
  recurrence: { occurrences: number } | null;
  /**
   * Create-time enrollment (issues #183/#259): student ids enrolled in the
   * same transaction (and, for a recurring series, in every instance).
   * `"assigned"` seats are membership-covered; `"open"` seats spend one
   * credit each unless `comp` is set. Ignored on PATCH.
   */
  student_ids?: number[];
  /**
   * Gift the selected `"open"` seats instead of spending credits (issue
   * #259). Ignored for `"assigned"` lessons and on PATCH.
   */
  comp?: boolean;
  /** The chosen instructor (issue #284); the form defaults it to the
   *  logged-in teacher on create and the lesson's current instructor on
   *  edit. */
  instructor_id: number;
}

export interface UpdateLessonVars {
  id: number;
  changes: Partial<LessonInput>;
}

/**
 * POST /api/lessons — create a lesson (teacher only). Returns the
 * created lesson in the standard shape (`spots_taken: 0`,
 * `spots_left: capacity`, `reserved_by_me: false`).
 */
export function useCreateLesson(
  token: string | null,
): UseMutationResult<Lesson, ApiRequestError, LessonInput> {
  const qc = useQueryClient();

  return useMutation<Lesson, ApiRequestError, LessonInput>({
    mutationFn: (input) => api.post<Lesson>("/api/lessons", input, { token }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.lessonsAll() });
    },
  });
}

/**
 * PATCH /api/lessons/:id — update a lesson (teacher only). The body is
 * any subset of the lesson fields; the path id is authoritative.
 */
export function useUpdateLesson(
  token: string | null,
): UseMutationResult<Lesson, ApiRequestError, UpdateLessonVars> {
  const qc = useQueryClient();

  return useMutation<Lesson, ApiRequestError, UpdateLessonVars>({
    mutationFn: ({ id, changes }) =>
      api.patch<Lesson>(`/api/lessons/${id}`, changes, { token }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.lessonsAll() });
    },
  });
}

/**
 * DELETE /api/lessons/:id — delete a lesson (teacher only). Cascades to
 * its reservations server-side. Returns `204 No Content`.
 */
export function useDeleteLesson(
  token: string | null,
): UseMutationResult<void, ApiRequestError, number> {
  const qc = useQueryClient();

  return useMutation<void, ApiRequestError, number>({
    mutationFn: (lessonId) => api.delete<void>(`/api/lessons/${lessonId}`, { token }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.lessonsAll() });
    },
  });
}

/**
 * Teacher-managed enrollment (issues #183/#184).
 *
 * `:assigned` lessons are not publicly visible and cannot be self-booked;
 * the teacher enrolls students instead. These hooks back the enrollment
 * picker (issue #186): list who is enrolled, add a student, remove one.
 * Enroll / un-enroll change `spots_taken`, so they invalidate both the
 * lesson's roster and every `lessons` query.
 */
export function useLessonStudents(
  token: string | null,
  lessonId: number | null,
): UseQueryResult<Student[], ApiRequestError> {
  return useQuery<Student[], ApiRequestError>({
    queryKey: queryKeys.lessonStudents(lessonId ?? 0),
    queryFn: ({ signal }) =>
      api.get<Student[]>(`/api/lessons/${lessonId}/students`, { token, signal }),
    enabled: Boolean(token) && lessonId !== null,
    staleTime: 30_000,
  });
}

export interface EnrollStudentVars {
  lessonId: number;
  studentId: number;
  /**
   * Seat an `:open` student without spending a credit (issue #245). Ignored
   * for `:assigned` lessons.
   */
  comp?: boolean;
}

/**
 * POST /api/lessons/:id/students — enroll a student (teacher only).
 * Returns the enrolled student in roster shape. An `:open` seat spends one
 * of the student's credits unless `comp` is set, so the student's ledger and
 * the roster are refreshed too.
 */
export function useEnrollStudent(
  token: string | null,
): UseMutationResult<Student, ApiRequestError, EnrollStudentVars> {
  const qc = useQueryClient();

  return useMutation<Student, ApiRequestError, EnrollStudentVars>({
    mutationFn: ({ lessonId, studentId, comp }) =>
      api.post<Student>(
        `/api/lessons/${lessonId}/students`,
        { student_id: studentId, ...(comp ? { comp: true } : {}) },
        { token },
      ),
    onSuccess: (_student, { lessonId, studentId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.lessonStudents(lessonId) });
      qc.invalidateQueries({ queryKey: queryKeys.lessonsAll() });
      qc.invalidateQueries({ queryKey: queryKeys.studentCredits(studentId) });
      qc.invalidateQueries({ queryKey: queryKeys.students() });
    },
  });
}

export interface UnenrollStudentVars {
  lessonId: number;
  studentId: number;
}

/**
 * DELETE /api/lessons/:id/students/:studentId — remove a student
 * (teacher only). Idempotent server-side; returns `204 No Content`.
 */
export function useUnenrollStudent(
  token: string | null,
): UseMutationResult<void, ApiRequestError, UnenrollStudentVars> {
  const qc = useQueryClient();

  return useMutation<void, ApiRequestError, UnenrollStudentVars>({
    mutationFn: ({ lessonId, studentId }) =>
      api.delete<void>(`/api/lessons/${lessonId}/students/${studentId}`, { token }),
    onSuccess: (_result, { lessonId, studentId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.lessonStudents(lessonId) });
      qc.invalidateQueries({ queryKey: queryKeys.lessonsAll() });
      // A removal refunds a credit-funded seat (issue #245).
      qc.invalidateQueries({ queryKey: queryKeys.studentCredits(studentId) });
      qc.invalidateQueries({ queryKey: queryKeys.students() });
    },
  });
}

/**
 * The teacher-editable subset of a student's profile (issue #88).
 * PATCH semantics: any subset of these fields. The form only ever
 * sends real strings — `name: null` is rejected server-side anyway.
 */
export type StudentChanges = Partial<{ name: string; email: string }>;

export interface UpdateStudentVars {
  id: number;
  changes: StudentChanges;
}

/**
 * PATCH /api/students/:id — edit a student's name/email (teacher
 * only, issue #88). Returns the updated student in the roster shape;
 * on success the `["students"]` query is invalidated so the roster
 * reflects the change immediately, and the lesson rosters are
 * invalidated too so a name/email edited from a lesson's attendee list
 * (issue #222) stays in sync there.
 */
export function useUpdateStudent(
  token: string | null,
): UseMutationResult<Student, ApiRequestError, UpdateStudentVars> {
  const qc = useQueryClient();

  return useMutation<Student, ApiRequestError, UpdateStudentVars>({
    mutationFn: ({ id, changes }) =>
      api.patch<Student>(`/api/students/${id}`, changes, { token }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.students() });
      qc.invalidateQueries({ queryKey: queryKeys.lessonsAll() });
    },
  });
}

/**
 * DELETE /api/students/:id — delete a student (teacher only, issue #307).
 *
 * A hard delete server-side: the account and its dependent rows
 * (bookings, notes, memberships, credits, tokens) are removed. Deleting
 * a student frees the seats they held, so both the roster and every
 * lessons query are invalidated. Returns `204 No Content`.
 */
export function useDeleteStudent(
  token: string | null,
): UseMutationResult<void, ApiRequestError, number> {
  const qc = useQueryClient();

  return useMutation<void, ApiRequestError, number>({
    mutationFn: (studentId) => api.delete<void>(`/api/students/${studentId}`, { token }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.students() });
      qc.invalidateQueries({ queryKey: queryKeys.lessonsAll() });
    },
  });
}

export interface BlockStudentVars {
  studentId: number;
  /** Optional teacher-facing reason, recorded for audit. */
  reason?: string;
}

/**
 * POST /api/students/:id/block — block a student account without deleting
 * it (teacher only, issue #310). Returns the updated student in the roster
 * shape; on success the roster is invalidated so the blocked badge shows
 * everywhere.
 */
export function useBlockStudent(
  token: string | null,
): UseMutationResult<Student, ApiRequestError, BlockStudentVars> {
  const qc = useQueryClient();

  return useMutation<Student, ApiRequestError, BlockStudentVars>({
    mutationFn: ({ studentId, reason }) =>
      api.post<Student>(
        `/api/students/${studentId}/block`,
        reason ? { reason } : {},
        { token },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.students() });
    },
  });
}

/**
 * POST /api/students/:id/unblock — lift a student's block (teacher only,
 * issue #310). Idempotent.
 */
export function useUnblockStudent(
  token: string | null,
): UseMutationResult<Student, ApiRequestError, number> {
  const qc = useQueryClient();

  return useMutation<Student, ApiRequestError, number>({
    mutationFn: (studentId) =>
      api.post<Student>(`/api/students/${studentId}/unblock`, {}, { token }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.students() });
    },
  });
}

/**
 * GET /api/students/:id/notes
 *
 * Teacher notes about a student (issue #105), newest first, each with
 * its author. Teacher-only endpoint; enabled only while the caller is
 * viewing a student's notes (e.g. the edit modal is open).
 */
export function useStudentNotes(
  token: string | null,
  studentId: number | null,
): UseQueryResult<StudentNote[], ApiRequestError> {
  return useQuery<StudentNote[], ApiRequestError>({
    queryKey: queryKeys.studentNotes(studentId ?? 0),
    queryFn: ({ signal }) =>
      api.get<StudentNote[]>(`/api/students/${studentId}/notes`, { token, signal }),
    enabled: Boolean(token) && studentId !== null,
    staleTime: 30_000,
  });
}

/**
 * GET /api/students/:id/reservations
 *
 * A student's booked lessons, ordered by `starts_at` ascending for
 * `upcoming` and descending for `past` (teacher-only, issue #223).
 * Backs the student detail view so the teacher can see how heavily
 * someone is booked and review their month-by-month history.
 */
export type StudentReservationsScope = "upcoming" | "past";

export function useStudentReservations(
  token: string | null,
  studentId: number | null,
  scope: StudentReservationsScope = "upcoming",
): UseQueryResult<Lesson[], ApiRequestError> {
  return useQuery<Lesson[], ApiRequestError>({
    queryKey: queryKeys.studentReservations(studentId ?? 0, scope),
    queryFn: ({ signal }) =>
      api.get<Lesson[]>(`/api/students/${studentId}/reservations`, {
        token,
        signal,
        query: { scope },
      }),
    enabled: Boolean(token) && studentId !== null,
    staleTime: 30_000,
  });
}

/**
 * POST /api/students/:id/notes — add a note about a student (teacher
 * only, issue #105). On success the student's notes query is
 * invalidated so the list refreshes in place (the edit modal stays
 * open while notes are added).
 */
export function useCreateStudentNote(
  token: string | null,
  studentId: number | null,
): UseMutationResult<StudentNote, ApiRequestError, string> {
  const qc = useQueryClient();

  return useMutation<StudentNote, ApiRequestError, string>({
    mutationFn: (body) =>
      api.post<StudentNote>(`/api/students/${studentId}/notes`, { body }, { token }),
    onSuccess: () => {
      if (studentId !== null) {
        qc.invalidateQueries({ queryKey: queryKeys.studentNotes(studentId) });
      }
    },
  });
}

/**
 * GET /api/me/credits
 *
 * The caller's **sanitized** credit summary (issue #246): balance plus a
 * neutral activity list of `{amount, inserted_at}`. Gated on the token.
 */
export function useMyCredits(
  token: string | null,
): UseQueryResult<StudentCreditSummary, ApiRequestError> {
  return useQuery<StudentCreditSummary, ApiRequestError>({
    queryKey: queryKeys.myCredits(),
    queryFn: ({ signal }) =>
      api.get<StudentCreditSummary>("/api/me/credits", { token, signal }),
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}

/**
 * GET /api/students/:id/credits
 *
 * The full teacher ledger for a student (issue #246), newest first.
 * Teacher-only; gated on a token and a student id.
 */
export function useStudentCredits(
  token: string | null,
  studentId: number | null,
): UseQueryResult<TeacherCreditSummary, ApiRequestError> {
  return useQuery<TeacherCreditSummary, ApiRequestError>({
    queryKey: queryKeys.studentCredits(studentId ?? 0),
    queryFn: ({ signal }) =>
      api.get<TeacherCreditSummary>(`/api/students/${studentId}/credits`, {
        token,
        signal,
      }),
    enabled: Boolean(token) && studentId !== null,
    staleTime: 30_000,
  });
}

export interface RechargeCreditsVars {
  studentId: number;
  amount: number;
  note?: string;
  /** Validity in calendar months; ignored when `expiresOn` is set (issue #327). */
  validMonths?: number;
  /** Explicit validity date `YYYY-MM-DD` (issue #327). */
  expiresOn?: string;
}

/**
 * POST /api/students/:id/credits — recharge a student's balance (teacher
 * only, issue #246). Returns the updated full ledger; on success the
 * student's credits and the roster (which shows the balance) are refreshed.
 */
export function useRechargeCredits(
  token: string | null,
): UseMutationResult<TeacherCreditSummary, ApiRequestError, RechargeCreditsVars> {
  const qc = useQueryClient();

  return useMutation<TeacherCreditSummary, ApiRequestError, RechargeCreditsVars>({
    mutationFn: ({ studentId, amount, note, validMonths, expiresOn }) =>
      api.post<TeacherCreditSummary>(
        `/api/students/${studentId}/credits`,
        {
          amount,
          note,
          valid_months: validMonths,
          expires_on: expiresOn,
        },
        { token },
      ),
    onSuccess: (_summary, { studentId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.studentCredits(studentId) });
      qc.invalidateQueries({ queryKey: queryKeys.students() });
    },
  });
}

export interface ReverseCreditVars {
  studentId: number;
  /** The funding entry to correct. */
  entryId: number;
  /** Positive magnitude to reverse; the server derives the sign. */
  amount: number;
  note?: string;
}

/**
 * POST /api/students/:id/credits/:entry_id/reversal — reverse a funding
 * entry, fully or partially (teacher only, issue #311). Returns the updated
 * full ledger; on success the student's credits and the roster (which shows
 * the balance) are refreshed.
 */
export function useReverseCredit(
  token: string | null,
): UseMutationResult<TeacherCreditSummary, ApiRequestError, ReverseCreditVars> {
  const qc = useQueryClient();

  return useMutation<TeacherCreditSummary, ApiRequestError, ReverseCreditVars>({
    mutationFn: ({ studentId, entryId, amount, note }) =>
      api.post<TeacherCreditSummary>(
        `/api/students/${studentId}/credits/${entryId}/reversal`,
        { amount, note },
        { token },
      ),
    onSuccess: (_summary, { studentId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.studentCredits(studentId) });
      qc.invalidateQueries({ queryKey: queryKeys.students() });
    },
  });
}

/**
 * GET /api/students/:id/memberships
 *
 * A student's membership periods, most recent first (teacher only,
 * issue #246).
 */
export function useStudentMemberships(
  token: string | null,
  studentId: number | null,
): UseQueryResult<Membership[], ApiRequestError> {
  return useQuery<Membership[], ApiRequestError>({
    queryKey: queryKeys.studentMemberships(studentId ?? 0),
    queryFn: ({ signal }) =>
      api.get<Membership[]>(`/api/students/${studentId}/memberships`, {
        token,
        signal,
      }),
    enabled: Boolean(token) && studentId !== null,
    staleTime: 30_000,
  });
}

export interface GrantMembershipVars {
  studentId: number;
  /** Calendar months to grant; defaults to `1` server-side. */
  months?: number;
  /** ISO-8601 date; overrides `months` when present. */
  endsOn?: string;
  note?: string;
}

/**
 * POST /api/students/:id/memberships — grant / extend a membership period
 * (teacher only, issue #246). On success the student's membership list and
 * the roster are refreshed.
 */
export function useGrantMembership(
  token: string | null,
): UseMutationResult<Membership, ApiRequestError, GrantMembershipVars> {
  const qc = useQueryClient();

  return useMutation<Membership, ApiRequestError, GrantMembershipVars>({
    mutationFn: ({ studentId, months, endsOn, note }) =>
      api.post<Membership>(
        `/api/students/${studentId}/memberships`,
        { months, ends_on: endsOn, note },
        { token },
      ),
    onSuccess: (_membership, { studentId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.studentMemberships(studentId) });
      qc.invalidateQueries({ queryKey: queryKeys.students() });
    },
  });
}

export interface CancelMembershipVars {
  studentId: number;
  membershipId: number;
}

/**
 * DELETE /api/students/:id/memberships/:membershipId — cancel a membership
 * period (teacher only, issue #246). On success the student's membership
 * list and the roster are refreshed.
 */
export function useCancelMembership(
  token: string | null,
): UseMutationResult<Membership, ApiRequestError, CancelMembershipVars> {
  const qc = useQueryClient();

  return useMutation<Membership, ApiRequestError, CancelMembershipVars>({
    mutationFn: ({ studentId, membershipId }) =>
      api.delete<Membership>(
        `/api/students/${studentId}/memberships/${membershipId}`,
        { token },
      ),
    onSuccess: (_membership, { studentId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.studentMemberships(studentId) });
      qc.invalidateQueries({ queryKey: queryKeys.students() });
    },
  });
}

/**
 * Pull the field-keyed validation errors out of a failed lesson
 * mutation. The contract's `422` body is `{"errors": {field: [msg]}}`;
 * anything else (401/403/404/500) yields an empty map and the screen
 * falls back to the error's `message`.
 */
export function lessonFieldErrors(err: unknown): Record<string, string[]> {
  if (err instanceof ApiRequestError && err.body?.errors) {
    return err.body.errors;
  }
  return {};
}

interface OptimisticCtx {
  snapshots: [readonly unknown[], Lesson[] | undefined][];
  mySnapshot: Lesson[] | undefined;
  /** Cached copy of the single-lesson (detail page) entry, if any. */
  lessonSnapshot: Lesson | undefined;
}

function applyBookOptimistic(
  old: Lesson[] | undefined,
  lessonId: number,
): Lesson[] | undefined {
  if (!old) return old;
  return old.map((l) =>
    l.id === lessonId
      ? optimisticBook(l)
      : l,
  );
}

function applyCancelOptimistic(
  old: Lesson[] | undefined,
  lessonId: number,
): Lesson[] | undefined {
  if (!old) return old;
  return old.map((l) =>
    l.id === lessonId
      ? optimisticCancel(l)
      : l,
  );
}

/**
 * Bump a lesson into the "I just booked it" state. The server is the
 * source of truth — `onSettled` always re-fetches, so any drift here
 * is short-lived. We do, however, guard against the optimistic update
 * running twice (e.g. user double-taps) so the displayed counts stay
 * monotonic.
 */
export function optimisticBook(l: Lesson): Lesson {
  if (l.reserved_by_me) return l;
  return {
    ...l,
    reserved_by_me: true,
    spots_taken: l.spots_taken + 1,
    spots_left: Math.max(l.spots_left - 1, 0),
  };
}

export function optimisticCancel(l: Lesson): Lesson {
  if (!l.reserved_by_me) return l;
  return {
    ...l,
    reserved_by_me: false,
    spots_taken: Math.max(l.spots_taken - 1, 0),
    spots_left: Math.min(l.spots_left + 1, l.capacity),
  };
}

/**
 * Inclusive studio-local date range for the analytics dashboard
 * (issue #292). Bounds are ISO-8601 calendar dates (`YYYY-MM-DD`); the
 * backend interprets them in the studio timezone.
 */
export interface AnalyticsRange {
  from: string;
  to: string;
}

/** Optional bucket filters for the analytics drill-down. */
export interface AnalyticsLessonFilters {
  /** Only lessons taught by this teacher. */
  instructorId?: number;
  /** Only lessons starting at this studio-local hour (0–23). */
  hour?: number;
}

/**
 * GET /api/analytics?from=&to= — the teacher's aggregate dashboard
 * payload (issue #292): period totals, fill rate / utilisation bucketed
 * by day, hour and instructor, the per-lesson breakdown, and the most
 * active students. Teacher-only, so it is gated on `token`.
 */
export function useAnalytics(
  token: string | null,
  range: AnalyticsRange,
): UseQueryResult<AnalyticsOverview, ApiRequestError> {
  return useQuery<AnalyticsOverview, ApiRequestError>({
    queryKey: queryKeys.analytics(range),
    queryFn: ({ signal }) =>
      api.get<AnalyticsOverview>("/api/analytics", {
        token,
        signal,
        query: { from: range.from, to: range.to },
      }),
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}

/**
 * GET /api/analytics/lessons?from=&to=&instructor_id=&hour= — the
 * per-lesson rows behind a dashboard bucket, most recent first
 * (issue #292). Backs the drill-down when a metric is tapped.
 */
export function useAnalyticsLessons(
  token: string | null,
  range: AnalyticsRange,
  filters: AnalyticsLessonFilters = {},
): UseQueryResult<AnalyticsLessonRow[], ApiRequestError> {
  const { instructorId, hour } = filters;

  return useQuery<AnalyticsLessonRow[], ApiRequestError>({
    queryKey: queryKeys.analyticsLessons(range, filters),
    queryFn: ({ signal }) =>
      api.get<AnalyticsLessonRow[]>("/api/analytics/lessons", {
        token,
        signal,
        query: {
          from: range.from,
          to: range.to,
          instructor_id: instructorId,
          hour,
        },
      }),
    enabled: Boolean(token),
    staleTime: 30_000,
  });
}
